<template>
    <div class="h-full flex flex-col gap-2 p-2">
        <div class="text-xl">Transaksjoner</div>
        <UInput
            v-model="searchText"
            placeholder="Kontonummer, beløp eller tekst"
            data-cy="transaksjons-filter"
            icon="i-ph-magnifying-glass"
            @update:model-value="applySearchText"
        />
        <div class="h-full flex gap-12">
            <div class="h-full flex flex-col grow">
                <div class="flex my-2">
                    <div class="grow"/>
                </div>
                <ag-grid-vue
                    class="h-full grow"
                    :theme="agTheme"
                    :column-defs="transactionColumns"
                    :row-data="sortedTransactions"
                    :grid-options="transactionsGridOptions"
                    row-selection="multiple"
                    suppress-row-click-selection
                    data-cy="transactions"
                />
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import {agTheme} from '@/agUtils/ag-theme.service'
import {AgGridVue} from 'ag-grid-vue3'
import {useTransactionService} from '@/bank/transaction.service'
import {computed, inject, onMounted, ref, Ref, shallowRef} from 'vue'
import {Transaction} from '@/bank/bank.types'
import _ from 'lodash'
import {formatCurrency} from '@/services/formatter.service'
import {ColDef, GridApi, GridOptions, GridReadyEvent, IRowNode} from 'ag-grid-community'
import Fuse from 'fuse.js'
import {globalDefaultColDef} from '@/agUtils'

type AgCellParam = {
    value?: any,
    data: Transaction
}

const {selectedAccount, transactions} = useTransactionService()

const transactionsGrid = shallowRef<GridApi>()
const panelId: Ref<number> | undefined = inject('panelId')
const panelPathSegment: Ref<number> | undefined = inject('panelPathSegment')

const searchText = ref<string>('')

const sortedTransactions = computed<Array<Transaction>>(() => {
    return _.sortBy(transactions.value, 'bookedAt').reverse()
})

const formatCurrencyCell = (param: AgCellParam) => formatCurrency(param.value)

const filterTransactionRowsBySearch = (node: IRowNode) => {
    const transaction = node.data
    if (searchText.value === '')
        return true
    if (transaction.debtorBban?.startsWith(searchText.value) || transaction.debtorIban?.startsWith(searchText.value) || transaction.creditorBban?.startsWith(searchText.value) || transaction.creditorIban?.startsWith(searchText.value))
        return true
    const fuse = new Fuse([
        transaction.debtorBban ?? '',
        transaction.debtorIban ?? '',
        transaction.creditorBban ?? '',
        transaction.creditorIban ?? '',
        formatCurrency(transaction.amount),
        transaction.remittanceInformation.message ?? '',
        transaction.remittanceInformation.reference ?? ''
    ], {includeScore: true, threshold: 0.3})

    return !!fuse.search(searchText.value).length
}

const applySearchText = () => {
    if (transactionsGrid.value) {
        transactionsGrid.value.onFilterChanged()
    }
}

const transactionColumns: ColDef[] = [
    {
        headerName: 'Dato',
        field: 'bookedAt',
        flex: 1
    },
    {
        headerName: 'Beløp',
        field: 'amount',
        cellRenderer: formatCurrencyCell,
        flex: 1
    },
    {
        headerName: 'Status',
        field: 'status',
        flex: 1
    }
]

const transactionsGridOptions: GridOptions = {
    animateRows: true,
    getRowId: (params: any) => params.data.uuid,
    isExternalFilterPresent: () => true,
    suppressCellFocus: true,
    context: {
        panelId,
        panelPathSegment
    },
    defaultColDef: {
        ...globalDefaultColDef,
        resizable: true,
        sortable: true
    },
    doesExternalFilterPass: filterTransactionRowsBySearch,
    onGridReady: (event: GridReadyEvent) => {
        transactionsGrid.value = event.api
    }
}

onMounted(() => {
    selectedAccount.value = {bban: '12024833710'}
})
</script>

<style scoped>

</style>